import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "../components/Top3";
import { toast } from "sonner";
import { RefreshCw, Sparkles } from "lucide-react";

const BUSINESSES = ["PYN CAPITAL", "DOA / PRO PARTNER", "BIOVAULT", "PERSONAL"];

const KIND_META = {
  daily_operator:      { code: "01", label: "DAILY OPERATOR REPORT",     caption: "What matters right now across all fronts." },
  weekly_executive:    { code: "02", label: "WEEKLY EXECUTIVE SNAPSHOT", caption: "Cross-business state · wins · drags." },
  business_scorecard:  { code: "03", label: "BUSINESS SCORECARD",        caption: "One front, one look. Pick the business." },
  waiting_on_report:   { code: "04", label: "WAITING ON REPORT",         caption: "Who's blocking progress, ranked by days." },
  money_collections:   { code: "05", label: "MONEY / COLLECTIONS",       caption: "Cash in · cash out · deals closing." },
};

const fmtWhen = (iso) => {
  if (!iso) return "NEVER";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).toUpperCase();
};

const ReportCard = ({ kind, business, onBusinessChange }) => {
  const meta = KIND_META[kind];
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.latestReport(kind, business);
      setReport(r && r.title ? r : null);
    } catch {
      setReport(null);
    }
  };
  useEffect(() => { load(); }, [kind, business]);

  const regenerate = async () => {
    setLoading(true);
    try {
      const r = await api.generateReport(kind, business);
      setReport(r);
      toast("Report generated", { description: r.source === "fallback" ? "Fallback used" : "Fresh from Claude." });
    } catch (e) {
      toast("Generation failed", { description: e?.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid={`report-${kind}`}>
      <SectionHeader code={meta.code} label={meta.label} caption={meta.caption} />
      <div className="op-panel p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            {report?.title && <h3 className="text-white text-2xl font-light tracking-tight mb-2">{report.title}</h3>}
            <div className="op-tag">
              {report ? `GENERATED · ${fmtWhen(report.generated_at)} · ${report.source?.toUpperCase()}` : "NOT YET GENERATED"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {kind === "business_scorecard" && (
              <select
                value={business || ""}
                onChange={(e) => onBusinessChange(e.target.value)}
                className="op-input op-mono text-xs !py-2"
                data-testid={`report-${kind}-business`}
              >
                <option value="">— PICK BUSINESS —</option>
                {BUSINESSES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            <button
              onClick={regenerate}
              disabled={loading || (kind === "business_scorecard" && !business)}
              data-testid={`report-${kind}-regenerate`}
              className="op-btn op-btn--primary inline-flex items-center gap-2 disabled:opacity-40"
            >
              <Sparkles size={12} className={loading ? "animate-pulse" : ""} />
              {loading ? "GENERATING…" : report ? "REGENERATE" : "GENERATE"}
            </button>
          </div>
        </div>

        {report ? (
          <div className="space-y-6" data-testid={`report-${kind}-body`}>
            {report.summary && (
              <p className="text-white text-base leading-relaxed max-w-3xl">{report.summary}</p>
            )}

            {report.highlights?.length > 0 && (
              <div>
                <div className="op-tag mb-3">HIGHLIGHTS</div>
                <ul className="space-y-2">
                  {report.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="op-mono text-[10px] text-[#5E657D] mt-1">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-white text-sm leading-snug">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {report.call_to_action && (
              <div className="border-l-2 border-l-[#00E5FF] pl-4 py-1">
                <div className="op-tag mb-1 text-[#00E5FF]">CALL TO ACTION</div>
                <div className="text-white text-base">{report.call_to_action}</div>
              </div>
            )}

            {report.stats && (
              <details className="pt-2">
                <summary className="op-tag cursor-pointer hover:text-[#00E5FF] select-none">
                  RAW STATS · CLICK TO EXPAND
                </summary>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  {Object.entries(report.stats).map(([k, v]) => {
                    if (v && typeof v === "object") return null;
                    return (
                      <div key={k} className="border border-white/[0.06] p-3">
                        <div className="op-tag mb-1">{k.replace(/_/g, " ").toUpperCase()}</div>
                        <div className="op-mono text-sm text-white">{typeof v === "number" && k.includes("total") ? `$${Number(v).toLocaleString()}` : String(v)}</div>
                      </div>
                    );
                  })}
                </div>
              </details>
            )}
          </div>
        ) : (
          <div className="text-[#5E657D] text-sm">
            {kind === "business_scorecard" && !business
              ? "Pick a business above, then generate a scorecard."
              : "No report yet. Click generate to build one from live data."}
          </div>
        )}
      </div>
    </section>
  );
};

const Reports = () => {
  const [scoreBiz, setScoreBiz] = useState("");

  return (
    <div className="px-6 md:px-12 py-10 md:py-14 max-w-[1600px] mx-auto space-y-12" data-testid="reports-page">
      <header>
        <div className="op-microcopy text-[#00E5FF] mb-4">AI · REPORTS</div>
        <h1 className="text-white text-4xl md:text-6xl font-light tracking-tight mb-3">Reports.</h1>
        <p className="text-[#A0A5B5] text-lg max-w-2xl">
          Auto-generated from live OS + Todoist data. Same priority hierarchy as War Room — money and people first.
        </p>
        <div className="op-hairline mt-8" />
      </header>

      <ReportCard kind="daily_operator" />
      <ReportCard kind="money_collections" />
      <ReportCard kind="waiting_on_report" />
      <ReportCard kind="business_scorecard" business={scoreBiz} onBusinessChange={setScoreBiz} />
      <ReportCard kind="weekly_executive" />
    </div>
  );
};

export default Reports;
